import type { Appointment } from '@/services/appointmentApi';
import { queryClient } from '@/lib/queryClient';
import { appointmentApi } from '@/services/appointmentApi';
import { appointmentKeys } from '@/hooks/useAppointments';

/**
 * Resolve which scheduled row is "the next one to start" right now.
 * Two flavors:
 *   - imminent: bounded ±visibility window — used by NextAppointmentDock
 *     so the dock only takes the right-rail when something is actually
 *     close. Late no-shows can still claim the dock briefly, but a 3pm
 *     slot doesn't shadow the page chrome at 9am.
 *   - scheduled: no upper window — used by the "start next" voice
 *     command, which fires regardless of how far away the next visit
 *     is. The doctor explicitly asked for it; clamp would surprise them
 *     ("MAIE, start next" at 9am with the next slot at 11am should still
 *     queue that 11am patient).
 *
 * Both flavors agree on:
 *   - skip completed / cancelled / no-show rows
 *   - sort by `time` (HH:MM) ascending
 *   - prefer the soonest upcoming (diff ≥ 0); fall back to a recently-
 *     late row when nothing's coming.
 */
export interface NextAppointmentOptions {
  /** Visibility window before the slot in minutes; null disables the cap. */
  windowBeforeMin?: number | null;
  /**
   * Grace window after the slot in minutes — late but un-completed rows
   * can take "next" up to this many minutes past the scheduled start.
   * Default 15 (matches the dock); set wider for voice resolution so a
   * 30-min late patient can still be voice-started.
   */
  windowAfterMin?: number;
}

const DEFAULT_AFTER_MIN = 15;

const toMinutes = (time?: string | null): number | null => {
  if (!time) return null;
  const [hh, mm] = time.split(':').map((n) => parseInt(n, 10));
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  return hh * 60 + mm;
};

export const pickNextAppointment = (
  appointments: readonly Appointment[],
  now: Date,
  opts: NextAppointmentOptions = {},
): Appointment | null => {
  if (appointments.length === 0) return null;
  const before = opts.windowBeforeMin ?? null;
  const after = opts.windowAfterMin ?? DEFAULT_AFTER_MIN;

  const sorted = [...appointments].sort((a, b) =>
    (a.time ?? '').localeCompare(b.time ?? ''),
  );
  const nowMin = now.getHours() * 60 + now.getMinutes();

  type Candidate = { apt: Appointment; diff: number };
  const candidates: Candidate[] = [];
  for (const a of sorted) {
    if (a.status === 'completed' || a.status === 'cancelled' || a.status === 'no-show') {
      continue;
    }
    const targetMin = toMinutes(a.time);
    if (targetMin == null) continue;
    const diff = targetMin - nowMin;
    if (before != null && diff > before) break; // sorted: rest is further future
    if (diff < -after) continue;
    candidates.push({ apt: a, diff });
  }
  if (candidates.length === 0) return null;

  const upcoming = candidates.find((c) => c.diff >= 0);
  if (upcoming) return upcoming.apt;
  return candidates[0].apt;
};

const todayStr = (now: Date): string => {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const TODAY_FILTER = (today: string) => ({
  limit: 100,
  startDate: today,
  endDate: today,
});

export interface ResolvedNextAppointment {
  appointment: Appointment;
  patientId: string;
  patientNameEn?: string;
  patientNameAr?: string;
}

/**
 * Headless resolver for the "start next" voice command. Reads today's
 * appointments through the shared react-query cache (so a fresh dock
 * mount and a wake-word dispatch see the same data), with a fetch
 * fallback if the cache is empty / cold. Returns null when nothing is
 * coming, or when the next row has no patientId (anonymous slot — the
 * voice command can't bind a patient to it).
 *
 * Window: no upper-before cap (a 3pm slot at 9am still resolves);
 * 30 min after-grace so a slightly late patient is still pick-able.
 */
export async function resolveNextAppointment(
  now: Date = new Date(),
): Promise<ResolvedNextAppointment | null> {
  const today = todayStr(now);
  const filter = TODAY_FILTER(today);

  const cached = queryClient.getQueryData<{ data: Appointment[] }>(
    appointmentKeys.list(filter),
  );

  let rows: Appointment[] = cached?.data ?? [];
  if (rows.length === 0) {
    try {
      const fresh = await queryClient.fetchQuery({
        queryKey: appointmentKeys.list(filter),
        queryFn: () => appointmentApi.getAppointments(filter),
        staleTime: 30_000,
      });
      rows = fresh?.data ?? [];
    } catch {
      return null;
    }
  }

  const next = pickNextAppointment(rows, now, {
    windowBeforeMin: null,
    windowAfterMin: 30,
  });
  if (!next || !next.patientId) return null;

  const inline = (next as Appointment & {
    patient?: { firstName?: string; lastName?: string };
  }).patient;
  const fullName = inline
    ? `${inline.firstName ?? ''} ${inline.lastName ?? ''}`.trim()
    : '';

  return {
    appointment: next,
    patientId: next.patientId,
    patientNameEn: fullName || undefined,
  };
}
